<template>
  <ul class="nav nav-tabs" role="tablist">
    <draggable
      v-model="tabList"
      draggable="li"
      animation="150"
      filter=".locked"
      :move="onMove"
      :disabled="!canDrag"
      @start="onDragStart"
      @end="onDragEnd"
      @dragenter.native="onDragEnter"
      @dragleave.native="onDragLeave"
      @drop.native="onDrop"
    >
      <li
        v-for="(tab, index) in tabList"
        :key="'tab-' + tab.slug"
        role="tab"
        :class="[
          'tab',
          { active: tab.id == selected || draggingOver == tab.id },
          { disabled: tab.disabled },
          { dimmed: tab.dimmed },
          { locked: tab.locked }
        ]"
        :aria-selected="tab.id == selected ? 'true' : 'false'"
        :tabindex="index"
      >
        <a
          href
          :class="{
            'drag-handle': true,
            'drag-cursor': canDrag
          }"
          :data-id="tab.id"
          :data-slug="tab.slug"
          @click.prevent="select(tab.id)"
        >
          <span v-if="tab.dimmed" class="af-icons danger af-icons-read-off"></span> &nbsp;
          {{ tab.name ? tab.name : '&nbsp;' }}

          <a class="settings-container" @click.prevent="onConfigure(tab)">
            <i class="af-icons af-icons-settings settings-background"></i>
          </a>
        </a>
      </li>
    </draggable>
    <span v-if="!configurationActive" class="add-tab">
      <a @click.prevent="insertTab">
        <i class="af-icons af-icons-add"></i>
      </a>
    </span>

    <confirmation-modal
      v-if="selectedTabSlug"
      :key="`modal-target-configure-tab-${selectedTabSlug}`"
      :modal-id="`modal-target-configure-field-${selectedTabSlug}`"
      :show-modal="confirmConfiguration"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmConfiguration = false"
      @confirmed="configure"
    />
  </ul>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { Tabs } from 'vue-bootstrap';
import Draggable from 'vuedraggable';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';

export default {
  inject: ['lang'],
  components: {
    Draggable,
    ConfirmationModal
  },
  extends: Tabs,
  props: {
    visibleTabs: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      dragging: false,
      draggingOver: null,
      selectedTabSlug: null,
      confirmConfiguration: false
    };
  },
  computed: {
    ...mapState('entryForm', ['tabTemplate']),
    ...mapState('entryFormConfiguration', [
      'tabsLocked',
      'configuredTab',
      'configurationInProgress'
    ]),
    ...mapGetters('entryFormConfiguration', ['configurationActive']),
    visibleTabIds() {
      return this.visibleTabs.map(tab => tab.id);
    },
    draggableTabs() {
      return this.tabs.map(tab => ({
        ...tab,
        disabled: false,
        dimmed: !this.visibleTabIds.includes(tab.id)
      }));
    },
    tabList: {
      get() {
        return this.draggableTabs || [];
      },
      set(tabs) {
        if (tabs && tabs.length) {
          this.saveTabOrder({ tabs });
        }
      }
    },
    canDrag() {
      return !this.configurationActive;
    }
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'saveTabOrder',
      'dropOnTheTop',
      'configureTab'
    ]),
    ...mapMutations('entryForm', ['selectTab', 'addTab']),
    ...mapMutations('entryFormConfiguration', ['setDraggedField']),
    select(id) {
      this.selected = id;
      this.$emit('selected', this.selected);
    },
    onDragStart() {
      this.dragging = true;
      this.setDraggedField();
    },
    onDragEnd() {
      this.dragging = false;
    },
    onDragEnter(event) {
      if (!this.canDrag || this.tabsLocked) {
        return;
      }
      const tabId =
        event.target instanceof Element
          ? parseInt(event.target.getAttribute('data-id'))
          : null;

      if (!this.dragging && tabId) {
        this.draggingOver = tabId;
        this.selectTab(tabId);
      }
    },
    onDragLeave() {
      this.draggingOver = null;
    },
    onDrop(event) {
      if (!this.canDrag) {
        return;
      }
      const tabSlug = event.target.getAttribute('data-slug');
      const tabId = parseInt(event.target.getAttribute('data-id'));
      this.dropOnTheTop({ tabSlug, tabId });
    },
    onMove(event) {
      const tab = this.draggableTabs[event.relatedContext.index];

      // Cancel the drag operation when attempting to move a tab to the position of a locked tab
      if (tab && tab.locked) {
        return false;
      }
    },
    onConfigure(tab) {
      if (tab.slug === this.selectedTabSlug && this.configurationActive) {
        return;
      }
      this.selectedTabSlug = tab.slug;

      if (this.configurationInProgress) {
        this.confirmConfiguration = true;
      } else {
        this.configure();
      }
    },
    configure() {
      this.configureTab({ slug: this.selectedTabSlug });
    },
    insertTab() {
      const slug = 'newtab';
      const order = this.tabList[this.tabList.length - 1].order + 1;

      this.addTab({ ...this.tabTemplate, slug, order });
      this.configureTab({ slug });
    }
  }
};
</script>

<style scoped>
.drag-cursor:hover {
  cursor: move;
}

.tab {
  position: relative;
}

.dimmed {
  opacity: 0.5;
}

.edit-tab {
  position: absolute;
  top: -35px;
  padding: 10px 0;
  width: 100%;
  display: none;
  background: transparent;
}

.tab:hover .edit-tab {
  display: inline-block;
}

.edit-tab i {
  font-size: 18px;
}

.settings-container {
  margin-right: 10px;
  color: #fff;
}
.settings-container:hover {
  margin-right: 8px;
}
.settings-background {
  margin-top: 3px;
  float: right;
}

.settings-background:hover {
  font-size: 16px;
  margin-top: 2px;
}

.add-tab i {
  font-size: 18px;
  padding-top: 18px;
  float: left;
}
</style>
